import 'styles/globals.css';
import 'destyle.css/destyle.min.css';
import { useContext } from 'react';

import type { AppProps } from 'next/app';
import Head from 'next/head';

import { ApolloProvider } from '@apollo/client';
import {
  ThemeProvider,
  StyledEngineProvider,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { ErrorBoundary } from 'react-error-boundary';

//
// Enable pages select Layout, see https://nextjs.org/docs/basic-features/layouts#with-typescript
import { client } from 'client/graphql';
import { Loading } from 'components/atoms/Loading';
import { DefaultError } from 'components/pages/DefaultError';
import { ConnectDataProvider } from 'hooks/global-state/connectData';
import {
  LoadingMutationProvider,
  LoadingContext,
} from 'hooks/global-state/loadingContext';
import { ReceiptListProvider } from 'hooks/global-state/receiptList';
import { ScrollPositionProvider } from 'hooks/global-state/scrollPosition';
import { SearchParamsContextProvider } from 'hooks/global-state/searchParamsContextProvider';
import { SnackbarContext, SnackbarProvider } from 'hooks/global-state/snackbar';
import { useDebugModeEnabled } from 'hooks/storage/useDebugModeEnabled';
import { useReloadIfNewVersionFound } from 'hooks/utils/useReloadIfNewAppVersionFound';
import { theme } from 'utils/mui/theme';
import 'utils/analysis/analysis'; // Amplitude の読み込み

import type { NextPageWithLayout } from 'utils/layout/types';

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  useReloadIfNewVersionFound();

  const { debugModeEnabled } = useDebugModeEnabled();

  const getLayout = Component.getLayout ?? ((page) => page);

  const onError = (error: Error, info: { componentStack: string }) => {
    // output error log
    console.log('error.message', error.message);
    console.log('info.componentStack:', info.componentStack);

    Sentry.captureException(error, {
      extra: {
        componentStack: info.componentStack,
      },
    });
  };

  return (
    <ErrorBoundary FallbackComponent={DefaultError} onError={onError}>
      <LoadingMutationProvider>
        <SnackbarProvider>
          <ApolloProvider client={client}>
            <ReceiptListProvider>
              <ScrollPositionProvider>
                <SearchParamsContextProvider>
                  <ConnectDataProvider>
                    <Head>
                      <link rel="shortcut icon" href="/favicon.ico" />
                    </Head>
                    <LoadingWithState />
                    <SnackbarWithState />
                    <ThemeProvider theme={theme}>
                      <StyledEngineProvider injectFirst>
                        {debugModeEnabled && (
                          // デバッグモードが暫定的な仕様である前提で以下を実装しているが、恒久仕様になる場合は適宜コンポーネント化を検討する
                          <Typography
                            bgcolor="yellow"
                            height={32}
                            color="red"
                            fontWeight={600}
                            fontSize={24}
                            textAlign="center"
                            position="sticky"
                            top={0}
                          >
                            デバッグモード
                          </Typography>
                        )}
                        {getLayout(<Component {...pageProps} />)}
                      </StyledEngineProvider>
                    </ThemeProvider>
                  </ConnectDataProvider>
                </SearchParamsContextProvider>
              </ScrollPositionProvider>
            </ReceiptListProvider>
          </ApolloProvider>
        </SnackbarProvider>
      </LoadingMutationProvider>
    </ErrorBoundary>
  );
}
const LoadingWithState = () => {
  const { isLoading } = useContext(LoadingContext);
  return <>{isLoading ? <Loading /> : <div />}</>;
};

const SnackbarWithState = () => {
  const { snackbar, onClose } = useContext(SnackbarContext);
  return (
    <Snackbar
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={onClose}
    >
      <Alert onClose={onClose} severity={snackbar.severity}>
        {snackbar.message}
      </Alert>
    </Snackbar>
  );
};

export default MyApp;
